/**
 * toLedgerView — pure folio + frozen-charges → ledger-view mapper
 * (SCRUM-336).
 *
 * The `LedgerTab` feature never consumes the raw folio / charges DTOs: it
 * consumes a `LedgerView` computed here. Keeping the projection PURE (no
 * hooks, no I/O, no wall-clock read) makes it unit-testable in isolation
 * and keeps the feature a thin renderer — the exact posture of
 * `to-reservation-detail-view.ts` (SCRUM-335) and `to-reservation-row-view.ts`
 * (SCRUM-334).
 *
 * The ledger has two INDEPENDENT regions (plan "two independent permission
 * regions"):
 *   - Region A — the frozen line-item ledger (from the charges read-model,
 *     `booking.reservations.read`). It self-describes its collection mode
 *     via the L1 `kind` discriminant, so it renders even when the folio is
 *     403 / 404.
 *   - Region B — the cash summary + authoritative merchant badge (from the
 *     folio read-model, `payments.folios.read`). Absent (`null`) when the
 *     folio is forbidden / not-found.
 *
 * MONEY (CLAUDE.md rule 6 / FE rule §6): every figure is a passed-through
 * server `Cents` field. The mapper NEVER sums, subtracts, or divides —
 * `totalContractValueCents` is `breakdown.grandTotalCents` verbatim,
 * `netRemittanceCents` is the frozen channel-remittance total verbatim,
 * a negative commission stays negative (the `LedgerLine` primitive paints
 * it danger). Absent Cents map to `null` (never `0`) so the feature renders
 * the neutral "—" marker via `formatCents`, never `formatCents(0)`.
 *
 * AHEAD-OF-BACKEND (plan "NEEDs"): the owner/PMC net-rent split, the
 * itemized ChannelCollect net remittance, and the channel gross / OTA
 * commission are all deferred (Accounting Phase 8 / channel Phase 11). They
 * surface as always-present honest `needs` flags and per-line `needNote`s
 * keyed off each `inertFees[].awaitedModule` — never fabricated numbers.
 */

import type { components } from '@artemis/contracts';
import type {
  ChannelRemittanceChargesDto,
  ComputedChargesDto,
  FolioReadModelDto,
  ReservationChargesResponseDto,
} from './types.js';

export type { FolioReadModelDto };

/**
 * Line-item sub-DTOs — the wire types are the single source of truth
 * (FE rule §4; never hand-typed). Derived from `components['schemas']`.
 */
type FeeLineItemResponseDto = components['schemas']['FeeLineItemResponseDto'];
type TaxLineItemResponseDto = components['schemas']['TaxLineItemResponseDto'];
type InertFeeInfoResponseDto = components['schemas']['InertFeeInfoResponseDto'];

/**
 * The frozen-charges body — the inner (unwrapped) discriminated union
 * `useReservationCharges` exposes and `toLedgerView` branches on. This is
 * the generated contract union verbatim (`ReservationChargesResponseDto`'s
 * `.data`: `computed | channel-remittance | block`); re-exported here so
 * downstream consumers keep one import site. Single source of truth is
 * `@artemis/contracts` (FE rule §4). The mapper narrows on `charges.kind`.
 */
export type ChargesBody = ReservationChargesResponseDto['data'];

/** One ledger line — label + a passed-through Cents figure. */
export interface LedgerLineEntry {
  /** Display label (guest-facing name for fees/taxes; role label for L2). */
  readonly label: string;
  /** Cents — passthrough. Negative for a commission deduction. */
  readonly valueCents: number;
  /** NEED annotation on a phase-gated (inert) fee line. */
  readonly needNote?: string;
}

/** An always-present honest NEED placeholder (no fabricated data). */
export interface NeedFlag {
  /** e.g. "Computed when Accounting (Phase 8) ships". */
  readonly label: string;
}

/** The view the `LedgerTab` feature renders. */
export interface LedgerView {
  /**
   * Authoritative merchant-of-record axis. From `folio.collectionModel`
   * when the folio is present; falls back to the charges `kind` (computed
   * → PropertyCollect, channel-remittance → ChannelCollect) so the ledger
   * shape is known even when the folio is 403 / 404; `null` for block /
   * fully-absent.
   */
  readonly collectionModel: 'PropertyCollect' | 'ChannelCollect' | null;
  /** Badge text (never color-only): PropertyCollect → PMC, ChannelCollect → OTA. */
  readonly merchantBadge: 'PMC' | 'OTA' | null;
  /** Region B — cash summary; `null` when the folio is forbidden / not-found. */
  readonly cashSummary: {
    readonly totalDueCents: number;
    readonly netCollectedCents: number;
    readonly balanceDueCents: number;
  } | null;
  /**
   * Region A — L2 wholesale owner matrix rows (Gross / commission(−) / Net).
   * `null` when the charges are absent / non-computed OR `ownerMatrix` is
   * null (OwnerStay). Commission is passed through NEGATIVE (never inverted).
   */
  readonly ownerMatrixLines: LedgerLineEntry[] | null;
  /** Region A — fee rows (guestFacingName + amountCents passthrough). */
  readonly feeLines: LedgerLineEntry[];
  /** Region A — tax rows (guestFacingName + amountCents passthrough). */
  readonly taxLines: LedgerLineEntry[];
  /** Region A — phase-gated (inert) fee rows, each with a NEED note. */
  readonly inertFeeLines: LedgerLineEntry[];
  /** `breakdown.grandTotalCents` (passthrough); `null` when charges absent / non-computed. */
  readonly totalContractValueCents: number | null;
  /** ChannelCollect — `folio.balanceDueCents === 0` (the "$0.00 balance" note). */
  readonly guestBalanceIsZero: boolean;
  /** ChannelCollect frozen total net remittance (passthrough); `null` otherwise. */
  readonly netRemittanceCents: number | null;
  /** True when the charges body is a NonStayBlock. */
  readonly isBlock: boolean;
  /** True when the charges read-model is absent (403 / 404 / null). */
  readonly isChargesAbsent: boolean;
  /** Always-present honest NEED placeholders (no fabricated data). */
  readonly needs: {
    /** Owner/PMC net-rent split — Accounting Phase 8. */
    readonly ownerPmcSplit: NeedFlag;
    /** Itemized ChannelCollect net remittance — Accounting Phase 8. */
    readonly itemizedNetRemittance: NeedFlag;
    /** Channel gross / OTA commission / channel-remitted taxes — channel Phase 11. */
    readonly channelGross: NeedFlag;
  };
}

const NEED_ACCOUNTING = 'Computed when Accounting (Phase 8) ships';
const NEED_CHANNEL = 'Computed when channel integration (Phase 11) ships';

/** The always-present NEED placeholders — identical on every branch. */
const NEEDS: LedgerView['needs'] = {
  ownerPmcSplit: { label: NEED_ACCOUNTING },
  itemizedNetRemittance: { label: NEED_ACCOUNTING },
  channelGross: { label: NEED_CHANNEL },
};

function isComputed(
  charges: ChargesBody | null,
): charges is ComputedChargesDto {
  return charges?.kind === 'computed';
}

function isChannelRemittance(
  charges: ChargesBody | null,
): charges is ChannelRemittanceChargesDto {
  return charges?.kind === 'channel-remittance';
}

/**
 * Map the frozen L2 wholesale matrix to Gross / commission(−) / Net rows.
 * `null` when `ownerMatrix` is absent (OwnerStay) — the L1 fee / tax lines
 * render independently (they do not depend on L2).
 */
function toOwnerMatrixLines(
  charges: ComputedChargesDto,
): LedgerLineEntry[] | null {
  const matrix = charges.ownerMatrix;
  if (matrix == null) {
    return null;
  }
  return [
    { label: 'Gross Room Rent', valueCents: matrix.grossRoomRentCents },
    // Commission is stored negative on the wire; passed through UNCHANGED
    // (never inverted) — the LedgerLine primitive paints a negative danger.
    { label: 'OTA Commission', valueCents: matrix.otaCommissionCents },
    { label: 'Net Room Rent', valueCents: matrix.netRoomRentCents },
  ];
}

function toFeeLines(charges: ComputedChargesDto): LedgerLineEntry[] {
  return charges.breakdown.feeLineItems.map(
    (fee: FeeLineItemResponseDto): LedgerLineEntry => ({
      label: fee.guestFacingName,
      valueCents: fee.amountCents,
    }),
  );
}

function toTaxLines(charges: ComputedChargesDto): LedgerLineEntry[] {
  return charges.breakdown.taxLineItems.map(
    (tax: TaxLineItemResponseDto): LedgerLineEntry => ({
      label: tax.guestFacingName,
      valueCents: tax.amountCents,
    }),
  );
}

function toInertFeeLines(charges: ComputedChargesDto): LedgerLineEntry[] {
  return charges.breakdown.inertFees.map(
    (inert: InertFeeInfoResponseDto): LedgerLineEntry => ({
      label: inert.guestFacingName,
      valueCents: inert.amountCentsHadItFired,
      needNote: inert.awaitedModule === 'accounting' ? NEED_ACCOUNTING : NEED_CHANNEL,
    }),
  );
}

/**
 * Derive the merchant-of-record axis. Prefer the authoritative folio
 * `collectionModel`; fall back to the charges `kind` when the folio is
 * absent (403 / 404) so the ledger shape survives a folio permission gap.
 * `block` / fully-absent → `null`.
 */
function deriveCollectionModel(
  folio: FolioReadModelDto | null,
  charges: ChargesBody | null,
): 'PropertyCollect' | 'ChannelCollect' | null {
  if (folio != null) {
    return folio.collectionModel;
  }
  if (isComputed(charges)) {
    return 'PropertyCollect';
  }
  if (isChannelRemittance(charges)) {
    return 'ChannelCollect';
  }
  return null;
}

export function toLedgerView(
  folio: FolioReadModelDto | null,
  charges: ChargesBody | null,
): LedgerView {
  const collectionModel = deriveCollectionModel(folio, charges);

  const merchantBadge: LedgerView['merchantBadge'] =
    collectionModel === 'PropertyCollect'
      ? 'PMC'
      : collectionModel === 'ChannelCollect'
        ? 'OTA'
        : null;

  const cashSummary: LedgerView['cashSummary'] =
    folio != null
      ? {
          totalDueCents: folio.totalDueCents,
          netCollectedCents: folio.netCollectedCents,
          balanceDueCents: folio.balanceDueCents,
        }
      : null;

  const computed = isComputed(charges) ? charges : null;

  return {
    collectionModel,
    merchantBadge,
    cashSummary,
    ownerMatrixLines: computed != null ? toOwnerMatrixLines(computed) : null,
    feeLines: computed != null ? toFeeLines(computed) : [],
    taxLines: computed != null ? toTaxLines(computed) : [],
    inertFeeLines: computed != null ? toInertFeeLines(computed) : [],
    totalContractValueCents:
      computed != null ? computed.breakdown.grandTotalCents : null,
    guestBalanceIsZero: folio != null ? folio.balanceDueCents === 0 : false,
    netRemittanceCents: isChannelRemittance(charges)
      ? charges.netRemittanceCents
      : null,
    isBlock: charges?.kind === 'block',
    isChargesAbsent: charges === null,
    needs: NEEDS,
  };
}
